import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { 
  Bell, 
  Send, 
  Users, 
  User, 
  Paperclip, 
  Calendar,
  RefreshCw,
  X,
  CheckCircle,
  FileText,
  Upload,
  Eye,
  Clock,
  ChevronRight
} from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

export const AdminNotificationsPage = () => {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  
  // Send notification modal
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [recipientType, setRecipientType] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Data for dropdowns
  const [teams, setTeams] = useState([]);
  const [staffList, setStaffList] = useState([]);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    fetchTeams();
    fetchStaff();
  }, []);

  const fetchNotifications = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/notifications`, {
        params: { page, limit: 20 },
        withCredentials: true
      });
      setNotifications(response.data.notifications);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/admin/teams`, { withCredentials: true });
      setTeams(response.data.teams || response.data || []);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/admin/staff`, { withCredentials: true });
      // Filter only active staff
      const activeStaff = (response.data || []).filter(s => s.status === 'active');
      setStaffList(activeStaff);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setAttachment(file);
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/notifications/upload-attachment`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setAttachmentUrl(response.data.url);
    } catch (error) {
      console.error('Failed to upload attachment:', error);
      setAttachment(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSendNotification = async () => {
    if (!recipientType || !message.trim()) {
      alert('Please select recipient and enter a message');
      return;
    }
    
    if (recipientType === 'team' && !selectedTeam) {
      alert('Please select a team');
      return;
    }
    
    if (recipientType === 'staff' && !selectedStaff) {
      alert('Please select a staff member');
      return;
    }
    
    setSending(true);
    
    try {
      await axios.post(`${API}/admin/notifications/send`, {
        recipient_type: recipientType,
        team_id: recipientType === 'team' ? selectedTeam : null,
        staff_id: recipientType === 'staff' ? selectedStaff : null,
        message: message.trim(),
        attachment_url: attachmentUrl || null
      }, { withCredentials: true });
      
      // Reset form and close modal
      setSendModalOpen(false);
      resetForm();
      fetchNotifications();
    } catch (error) {
      console.error('Failed to send notification:', error);
      alert(error.response?.data?.detail || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setRecipientType('');
    setSelectedTeam('');
    setSelectedStaff('');
    setMessage('');
    setAttachment(null);
    setAttachmentUrl('');
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper to get correct attachment URL (handles both old and new formats)
  const getAttachmentUrl = (attachmentUrl) => {
    if (!attachmentUrl) return null;
    // If URL already starts with /api, don't add API prefix again
    if (attachmentUrl.startsWith('/api/')) {
      return `${API}${attachmentUrl.substring(4)}`; // Remove /api from start
    }
    return `${API}${attachmentUrl}`;
  };

  return (
    <div className="relative pb-8" data-testid="admin-notifications-page">
      <div className="pointer-events-none absolute inset-x-0 -top-6 h-56 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.12),_transparent_60%)]" />

      <div className="relative space-y-5 md:space-y-6">
      <section className="att-fade-up overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_50px_-28px_rgba(15,23,42,0.35)] backdrop-blur">
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 py-4 text-white sm:px-6 sm:py-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight sm:text-3xl">
                <Bell className="h-7 w-7" />
                Notifications
              </h1>
              <p className="mt-1.5 text-sm text-indigo-50/90">
                Send and manage notifications to your team
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="ghost"
                onClick={() => fetchNotifications()}
                disabled={loading}
                className="h-11 rounded-2xl bg-white/15 text-white ring-1 ring-white/25 hover:bg-white/25"
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                onClick={() => setSendModalOpen(true)}
                className="h-11 rounded-2xl bg-white px-5 font-semibold text-indigo-700 shadow-md hover:bg-indigo-50"
                data-testid="send-notification-btn"
              >
                <Send className="mr-2 h-4 w-4" />
                Send Notification
              </Button>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50/70 px-4 py-3 sm:px-6">
          <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">
            {pagination.total || notifications.length} sent
          </span>
        </div>
      </section>

      <section className="att-fade-up overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-sm" style={{ animationDelay: '80ms' }}>
        <div className="border-b border-slate-100 px-5 py-4">
          <h3 className="text-lg font-bold text-slate-900">Sent Notifications</h3>
          <p className="mt-1 text-sm text-slate-500">History of all notifications sent to teams and staff</p>
        </div>
        <div className="p-4 sm:p-5">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 py-16 text-center">
              <Bell className="mx-auto mb-4 h-12 w-12 text-slate-300" />
              <h3 className="text-lg font-bold text-slate-900">No notifications sent yet</h3>
              <p className="mt-1 text-sm text-slate-500">Click “Send Notification” to get started</p>
            </div>
          ) : (
            <>
              <TooltipProvider>
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80">
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Attachment</TableHead>
                      <TableHead>Recipients</TableHead>
                      <TableHead>{t("common.status")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notifications.map((notif) => (
                      <TableRow key={notif.notification_id}>
                        <TableCell className="whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Calendar className="h-4 w-4 text-slate-400" />
                            {formatDate(notif.created_at)}
                          </div>
                        </TableCell>
                        <TableCell>
                          {notif.recipient_type === 'team' ? (
                            <button
                              onClick={() => navigate(`/admin/notifications/${notif.notification_id}/recipients`)}
                              className="flex items-center gap-1 font-semibold text-purple-700 hover:underline"
                            >
                              {notif.recipient_name}
                              <ChevronRight className="h-4 w-4" />
                            </button>
                          ) : (
                            <div className="font-semibold text-slate-900">{notif.recipient_name}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            notif.recipient_type === 'team' 
                              ? 'bg-purple-50 text-purple-700 border-purple-200' 
                              : 'bg-sky-50 text-sky-700 border-sky-200'
                          }>
                            {notif.recipient_type === 'team' ? (
                              <Users className="mr-1 h-3 w-3" />
                            ) : (
                              <User className="mr-1 h-3 w-3" />
                            )}
                            {notif.recipient_type === 'team' ? notif.recipient_name : 'Individual'}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-xs">
                          <p className="truncate text-sm text-slate-700">{notif.message}</p>
                        </TableCell>
                        <TableCell>
                          {notif.attachment_url ? (
                            <a 
                              href={getAttachmentUrl(notif.attachment_url)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 font-medium text-purple-700 hover:text-purple-800"
                            >
                              <Paperclip className="h-4 w-4" />
                              View
                            </a>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="rounded-full">
                            {notif.read_count || 0}/{notif.total_recipients || notif.recipients?.length || 0} read
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {notif.all_read ? (
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge className="cursor-pointer bg-indigo-100 text-indigo-700 hover:bg-indigo-100">
                                  <Eye className="mr-1 h-3 w-3" />
                                  Read
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Read at: {notif.latest_read_at ? formatDate(notif.latest_read_at) : 'Unknown'}</p>
                              </TooltipContent>
                            </Tooltip>
                          ) : notif.read_count > 0 ? (
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge className="cursor-pointer bg-amber-100 text-amber-700 hover:bg-amber-100">
                                  <Clock className="mr-1 h-3 w-3" />
                                  Partial
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{notif.read_count} of {notif.total_recipients} read</p>
                                {notif.latest_read_at && <p>Last read: {formatDate(notif.latest_read_at)}</p>}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-600">
                              <Send className="mr-1 h-3 w-3" />
                              Sent
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              </TooltipProvider>
              
              {pagination.total_pages > 1 && (
                <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
                  <p className="text-sm text-slate-500">
                    Page {pagination.page} of {pagination.total_pages} ({pagination.total} total)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl"
                      disabled={pagination.page === 1}
                      onClick={() => fetchNotifications(pagination.page - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl"
                      disabled={pagination.page === pagination.total_pages}
                      onClick={() => fetchNotifications(pagination.page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Send Notification Modal */}
      <Dialog open={sendModalOpen} onOpenChange={(open) => { if (!sending) setSendModalOpen(open); }}>
        <DialogContent className="overflow-hidden rounded-3xl border-0 p-0 shadow-2xl sm:max-w-lg" data-testid="send-notification-modal">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-6 py-5 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white">
                <Send className="h-5 w-5" />
                Send Notification
              </DialogTitle>
              <DialogDescription className="text-indigo-50/90">
                Send a notification to a team or individual staff member
              </DialogDescription>
            </DialogHeader>
          </div>
          
          <div className="space-y-4 px-6 py-5">
            {/* Recipient Type */}
            <div className="space-y-2">
              <Label>Notification To</Label>
              <Select value={recipientType} onValueChange={(val) => {
                setRecipientType(val);
                setSelectedTeam('');
                setSelectedStaff('');
              }}>
                <SelectTrigger data-testid="recipient-type-select">
                  <SelectValue placeholder="Select recipient type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="team">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-purple-600" />
                      Team
                    </div>
                  </SelectItem>
                  <SelectItem value="staff">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-blue-600" />
                      Staff
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Team Selection */}
            {recipientType === 'team' && (
              <div className="space-y-2">
                <Label>Select Team</Label>
                <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                  <SelectTrigger data-testid="team-select">
                    <SelectValue placeholder="Choose a team" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map((team) => (
                      <SelectItem key={team.team_id} value={team.team_id}>
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {teams.length === 0 && (
                  <p className="text-sm text-amber-600">No teams found. Create a team first.</p>
                )}
              </div>
            )}

            {/* Staff Selection */}
            {recipientType === 'staff' && (
              <div className="space-y-2">
                <Label>Select Staff</Label>
                <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                  <SelectTrigger data-testid="staff-select">
                    <SelectValue placeholder="Choose a staff member" />
                  </SelectTrigger>
                  <SelectContent>
                    {staffList.map((staff) => (
                      <SelectItem key={staff.staff_id} value={staff.staff_id}>
                        <div className="flex items-center gap-2">
                          <span>{staff.name}</span>
                          {staff.department && (
                            <span className="text-gray-400">({staff.department})</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {staffList.length === 0 && (
                  <p className="text-sm text-amber-600">No active staff found.</p>
                )}
              </div>
            )}

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter your notification message..."
                rows={4}
                data-testid="notification-message-input"
              />
            </div>

            {/* Attachment */}
            <div className="space-y-2">
              <Label>Attachment (Optional)</Label>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex-1"
                >
                  {uploading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      {attachment ? 'Change File' : 'Upload File'}
                    </>
                  )}
                </Button>
                {attachment && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
                    <FileText className="w-4 h-4 text-gray-500" />
                    <span className="text-sm truncate max-w-[150px]">{attachment.name}</span>
                    <button
                      onClick={() => {
                        setAttachment(null);
                        setAttachmentUrl('');
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 border-t border-slate-100 px-6 py-4 sm:space-x-0">
            <Button
              variant="outline"
              className="h-11 rounded-2xl"
              onClick={() => {
                setSendModalOpen(false);
                resetForm();
              }}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendNotification}
              disabled={sending || !recipientType || !message.trim()}
              className="h-11 rounded-2xl bg-indigo-600 hover:bg-indigo-700"
              data-testid="confirm-send-notification-btn"
            >
              {sending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send Notification
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default AdminNotificationsPage;
